import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import SocialAuthButtons from "@/components/auth/SocialAuthButtons";
import PasswordInput from "@/components/auth/PasswordInput";
import { toast } from "sonner";
import { z } from "zod";
import { ArrowLeft } from "lucide-react";

// Updated schema removing 'name'
const signupSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  confirmPassword: z.string(),
  acceptTerms: z.boolean().refine((val) => val === true),
  acceptEmails: z.boolean().refine((val) => val === true),
}).refine((data) => data.password === data.confirmPassword, {
  path: ["confirmPassword"],
});

const Signup = () => {
  const { t } = useTranslation();
  const { signUp, user } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Removed name state
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptEmails, setAcceptEmails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Shared password visibility state
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = signupSchema.safeParse({ email, password, confirmPassword, acceptTerms, acceptEmails });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const path = err.path[0] as string;
        if (path === "email") fieldErrors.email = t("auth.errors.invalidEmail");
        if (path === "password") fieldErrors.password = t("auth.errors.passwordMin");
        if (path === "confirmPassword") fieldErrors.confirmPassword = t("auth.errors.passwordMismatch");
        if (path === "acceptTerms") fieldErrors.acceptTerms = t("auth.errors.termsRequired");
        if (path === "acceptEmails") fieldErrors.acceptEmails = t("auth.errors.emailConsentRequired");
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    // Pass empty string or undefined for name since it's removed from UI
    const { error } = await signUp(email, password, "");
    setLoading(false);

    if (error) {
      if (error.message.includes("User already registered")) {
        toast.error(t("auth.errors.userExists"));
      } else {
        toast.error(error.message);
      }
    } else {
      toast.success(t("auth.signupSuccess"));
      navigate("/verify-email");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[hsl(var(--accent-pink))] rounded-2xl border-2 border-foreground p-8">
          <Link to="/" className="logo block text-center mb-6">
            Vesper
          </Link>
          
          <h1 className="heading-card text-center mb-8">{t("auth.signUp")}</h1>

          <SocialAuthButtons />

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-foreground/30"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[hsl(var(--accent-pink))] px-4 text-foreground/70">{t("auth.orContinueWith")}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Removed Name Input Field */}

            <div>
              <label className="block text-sm font-medium mb-2">{t("auth.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-full border-2 border-foreground bg-background text-foreground placeholder:text-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t("auth.emailPlaceholder")}
              />
              {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
            </div>

            <PasswordInput
              label={t("auth.password")}
              value={password}
              onChange={setPassword}
              placeholder={t("auth.passwordPlaceholder")}
              error={errors.password}
              showPasswordExternal={showPassword}
              onShowPasswordChange={setShowPassword}
            />

            <PasswordInput
              label={t("auth.confirmPassword")}
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder={t("auth.confirmPasswordPlaceholder")}
              error={errors.confirmPassword}
              showPasswordExternal={showPassword}
              onShowPasswordChange={setShowPassword}
            />

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="w-4 h-4 min-w-4 min-h-4 mt-0.5 flex-shrink-0 rounded border-2 border-foreground bg-background text-[hsl(var(--accent-cyan))] focus:ring-[hsl(var(--accent-cyan))] accent-[hsl(var(--accent-cyan))]"
              />
              <label htmlFor="terms" className="text-sm">
                {t("auth.acceptTerms")}{" "}
                <Link to="/terms" className="font-semibold hover:underline">{t("auth.termsOfService")}</Link>
                {" & "}
                <Link to="/privacy" className="font-semibold hover:underline">{t("auth.privacyPolicy")}</Link>
              </label>
            </div>
            {errors.acceptTerms && <p className="text-sm text-red-600">{errors.acceptTerms}</p>}

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="emailConsent"
                checked={acceptEmails}
                onChange={(e) => setAcceptEmails(e.target.checked)}
                className="w-4 h-4 min-w-4 min-h-4 mt-0.5 flex-shrink-0 rounded border-2 border-foreground bg-background text-[hsl(var(--accent-cyan))] focus:ring-[hsl(var(--accent-cyan))] accent-[hsl(var(--accent-cyan))]"
              />
              <label htmlFor="emailConsent" className="text-sm">
                {t("auth.acceptEmails")}
              </label>
            </div>
            {errors.acceptEmails && <p className="text-sm text-red-600">{errors.acceptEmails}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary justify-center disabled:opacity-50 hover:bg-[hsl(var(--accent-cyan))]"
            >
              {loading ? t("auth.loading") : t("auth.signUp")}
            </button>
          </form>

          <p className="text-center mt-6 text-sm">
            {t("auth.hasAccount")}{" "}
            <Link to="/login" className="font-semibold hover:underline">
              {t("auth.login")}
            </Link>
          </p>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-foreground/70 hover:text-foreground inline-flex items-center gap-2 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {t("auth.backToHome")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
